import classNames from "classnames";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { LoadingSpinner } from "PFComponents/loading_spinner";
import TooltipIcon from "PFComponents/tooltip_icon/tooltip_icon";
import CheckIcon from "PFIcons/check.svg";
import InfoIcon from "PFIcons/info.svg";
import RightArrowIcon from "PFIcons/right_arrow.svg";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "../audit_planner_page.module.scss";
import AllocationButtonDropdown from "./allocation_button_dropdown";

const AllocationButton = ({ workflowAutomationContext }) => {
  const { workflowAutomation, startWorkflowAutomation, setAlwaysAllocate, allocationStatus } =
    workflowAutomationContext;
  const { t } = useTranslation("auditPlanner", { keyPrefix: "allocationButton" });
  const [showDropdown, setShowDropdown] = useState(false);

  if (!workflowAutomation) {
    return <LoadingDots />;
  }

  const { pending, auto_filling, auto_shortlisting } = workflowAutomation;
  const isAlwaysAllocateOn = allocationStatus === "autorun";
  const allocationInProgress = allocationStatus === "allocating";
  const allocationCompleted = allocationStatus === "completed";
  const allocationShortlisting = allocationStatus === "autoShortlisting";
  const allocationReady =
    !allocationInProgress && allocationStatus !== "pending" && allocationStatus !== "completed";

  const text = t(`status.${allocationStatus}`);
  const rolesCountText = t(`rolesCountText.${allocationStatus}`, {
    pending: `<b>${pending}</b>`,
    autoShortlisting: `<b>${auto_shortlisting}</b>`,
    autoFilling: `<b>${auto_filling}</b>`,
    total: `<b>${auto_shortlisting + pending}</b>`
  });
  return (
    <>
      <div
        className={classNames(css.infoContainer, {
          [css.completed]: allocationCompleted,
          [css.autoShortlisting]: allocationShortlisting
        })}
      >
        <div dangerouslySetInnerHTML={{ __html: rolesCountText }} className={css.infoText} />
        {isAlwaysAllocateOn && (
          <TooltipIcon
            height={20}
            width={20}
            className={css.info}
            IconComponent={InfoIcon}
            content={t("alwaysOnTip")}
          />
        )}
      </div>
      <div className={css.inlineBlock}>
        <Button
          kind="tertiary"
          theme={{
            root: classNames(css.allocationButton, {
              [css.completed]: allocationCompleted,
              [css.allocating]: allocationInProgress
            })
          }}
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          {isAlwaysAllocateOn ? (
            <div className={css.pill} />
          ) : (
            <>
              {allocationReady && (
                <RightArrowIcon viewBox="5 5 15 15" height="20" className={css.allocationButtonIcon} />
              )}
              {allocationInProgress && <LoadingSpinner height="20" className={css.allocationButtonIcon} />}
              {allocationCompleted && (
                <CheckIcon
                  height="20"
                  className={css.allocationButtonIcon}
                  style={{ color: "var(--Button-primary-bg)" }}
                />
              )}
            </>
          )}
          {text}
        </Button>
        {showDropdown && (
          <AllocationButtonDropdown
            onClose={() => setShowDropdown(false)}
            isAlwaysAllocateOn={isAlwaysAllocateOn}
            startWorkflowAutomation={startWorkflowAutomation}
            setAlwaysAllocate={setAlwaysAllocate}
            allocationReady={allocationReady}
          />
        )}
      </div>
    </>
  );
};

AllocationButton.propTypes = {
  workflowAutomationContext: PropTypes.shape({
    startWorkflowAutomation: PropTypes.func.isRequired,
    setAlwaysAllocate: PropTypes.func.isRequired,
    workflowAutomation: PropTypes.shape({
      auto_filling: PropTypes.number.isRequired,
      auto_shortlisting: PropTypes.number.isRequired,
      pending: PropTypes.number.isRequired,
      auto_run: PropTypes.bool
    }).isRequired,
    isAuditAdmin: PropTypes.bool,
    setWorkflowAutomation: PropTypes.func,
    fetchWorkflowAutomation: PropTypes.func,
    allocationStatus: PropTypes.string
  })
};

export default AllocationButton;
